import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../contexts/ToastContext';
import productApi from '../../services/productApi';
import IngredientPicker from './IngredientPicker';
import Drawer from '../common/Drawer';
import './CreateProductModal.css';

const COST_FIELDS = [
  { id: 'coffee_cost',     label: 'Coffee' },
  { id: 'ingredient_cost', label: 'Ingredients' },
  { id: 'can_cost',        label: 'Can' },
  { id: 'packing_cost',    label: 'Packing' },
];

const marginClass = (m) => {
  if (m === null || m === undefined) return '';
  if (m >= 30) return 'good';
  if (m >= 15) return 'warn';
  return 'bad';
};

const CreateProductModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      flavor: '',
      coffee_cost: '',
      ingredient_cost: '',
      can_cost: '',
      packing_cost: '',
      wholesale_price: 3.15,
      retail_price: 6.25,
    },
  });

  const [isActive, setIsActive] = useState(true);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const coffeeCost      = parseFloat(watch('coffee_cost'))      || 0;
  const ingredientCost  = parseFloat(watch('ingredient_cost'))  || 0;
  const canCost         = parseFloat(watch('can_cost'))         || 0;
  const packingCost     = parseFloat(watch('packing_cost'))     || 0;
  const wholesalePrice  = parseFloat(watch('wholesale_price'))  || 0;
  const retailPrice     = parseFloat(watch('retail_price'))     || 0;

  const totalCogs = useMemo(
    () => coffeeCost + ingredientCost + canCost + packingCost,
    [coffeeCost, ingredientCost, canCost, packingCost]
  );

  const wholesaleMargin = wholesalePrice > 0
    ? ((wholesalePrice - totalCogs) / wholesalePrice) * 100
    : null;

  const retailMargin = retailPrice > 0
    ? ((retailPrice - totalCogs) / retailPrice) * 100
    : null;

  useEffect(() => {
    if (isOpen) {
      reset();
      setIsActive(true);
      setSelectedIngredients([]);
      setApiError(null);
    }
  }, [isOpen, reset]);

  const onSubmit = async (formData) => {
    const badIngredient = selectedIngredients.some(
      (ing) => !ing.quantity_per_keg || parseFloat(ing.quantity_per_keg) <= 0
    );
    if (badIngredient) {
      setApiError('All ingredients must have a quantity greater than 0');
      return;
    }

    setIsSubmitting(true);
    setApiError(null);
    try {
      const payload = {
        ...formData,
        is_active: isActive,
        total_cogs: totalCogs,
        ingredients: selectedIngredients.length > 0
          ? selectedIngredients.map((ing) => ({
              ingredient_id: ing.ingredient_id,
              quantity_per_keg: parseFloat(ing.quantity_per_keg),
            }))
          : undefined,
      };
      const created = await productApi.create(payload);
      showSuccess(`"${created.flavor}" created`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['production-capacity'] });
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e) => e.msg).join(', ')
        : detail || err.message || 'Failed to create product';
      setApiError(msg);
      showError('Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      <button
        type="button"
        className="app-drawer-btn app-drawer-btn--secondary"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="app-drawer-btn app-drawer-btn--primary"
        onClick={handleSubmit(onSubmit)}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Creating…' : 'Create Product'}
      </button>
    </>
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="New Product" footer={footer}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>

            {apiError && (
              <div className="cpm-error" role="alert">{apiError}</div>
            )}

            {/* ── Identity ── */}
            <div className="cpm-section">
              <div className="cpm-identity-row">
                <div className="cpm-field cpm-field-grow">
                  <label className="cpm-label" htmlFor="flavor">
                    Product Name <span className="cpm-required">*</span>
                  </label>
                  <input
                    id="flavor"
                    autoFocus
                    className={`cpm-input ${errors.flavor ? 'cpm-input-error' : ''}`}
                    placeholder="e.g. Baked Apple Cinnamon"
                    {...register('flavor', {
                      required: 'Name is required',
                      maxLength: { value: 100, message: 'Max 100 characters' },
                    })}
                  />
                  {errors.flavor && (
                    <span className="cpm-field-error">{errors.flavor.message}</span>
                  )}
                </div>

                <div className="cpm-field">
                  <label className="cpm-label">Status</label>
                  <button
                    type="button"
                    className={`cpm-toggle ${isActive ? 'on' : 'off'}`}
                    onClick={() => setIsActive((v) => !v)}
                    aria-pressed={isActive}
                  >
                    <span className="cpm-toggle-track">
                      <span className="cpm-toggle-knob" />
                    </span>
                    <span className="cpm-toggle-text">{isActive ? 'Active' : 'Inactive'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* ── Pricing ── */}
            <div className="cpm-section">
              <p className="cpm-section-label">
                Pricing
              </p>
              <div className="cpm-two-col">
                <div className="cpm-field">
                  <label className="cpm-label" htmlFor="wholesale_price">Wholesale</label>
                  <div className="cpm-prefix-wrap">
                    <span className="cpm-prefix">$</span>
                    <input
                      id="wholesale_price"
                      type="number" step="0.01" min="0"
                      className={`cpm-input cpm-input-prefixed ${errors.wholesale_price ? 'cpm-input-error' : ''}`}
                      placeholder="3.15"
                      {...register('wholesale_price', { min: { value: 0, message: 'Must be ≥ 0' }, valueAsNumber: true })}
                    />
                  </div>
                </div>
                <div className="cpm-field">
                  <label className="cpm-label" htmlFor="retail_price">Retail</label>
                  <div className="cpm-prefix-wrap">
                    <span className="cpm-prefix">$</span>
                    <input
                      id="retail_price"
                      type="number" step="0.01" min="0"
                      className={`cpm-input cpm-input-prefixed ${errors.retail_price ? 'cpm-input-error' : ''}`}
                      placeholder="6.25"
                      {...register('retail_price', { min: { value: 0, message: 'Must be ≥ 0' }, valueAsNumber: true })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Costs ── */}
            <div className="cpm-section">
              <p className="cpm-section-label">
                Cost Breakdown <span className="cpm-section-hint">per can</span>
              </p>
              <div className="cpm-two-col">
                {COST_FIELDS.map(({ id, label }) => (
                  <div className="cpm-field" key={id}>
                    <label className="cpm-label" htmlFor={id}>{label}</label>
                    <div className="cpm-prefix-wrap">
                      <span className="cpm-prefix">$</span>
                      <input
                        id={id}
                        type="number" step="0.01" min="0"
                        className="cpm-input cpm-input-prefixed"
                        placeholder="0.00"
                        {...register(id, { min: { value: 0, message: 'Must be ≥ 0' }, valueAsNumber: true })}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Live summary */}
              <div className="cpm-summary">
                <div className="cpm-summary-item">
                  <span className="cpm-summary-label">Total COGS</span>
                  <span className="cpm-summary-value">${totalCogs.toFixed(2)}</span>
                </div>
                <div className="cpm-summary-sep" />
                <div className="cpm-summary-item">
                  <span className="cpm-summary-label">W/Sale margin</span>
                  <span className={`cpm-summary-value cpm-margin-${marginClass(wholesaleMargin)}`}>
                    {wholesaleMargin !== null ? `${wholesaleMargin.toFixed(1)}%` : '—'}
                  </span>
                </div>
                <div className="cpm-summary-sep" />
                <div className="cpm-summary-item">
                  <span className="cpm-summary-label">Retail margin</span>
                  <span className={`cpm-summary-value cpm-margin-${marginClass(retailMargin)}`}>
                    {retailMargin !== null ? `${retailMargin.toFixed(1)}%` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Recipe ── */}
            <div className="cpm-section cpm-section-last">
              <p className="cpm-section-label">
                Recipe <span className="cpm-section-hint">optional</span>
              </p>
              <IngredientPicker
                selectedIngredients={selectedIngredients}
                onIngredientsChange={setSelectedIngredients}
              />
            </div>

          </form>
    </Drawer>
  );
};

export default CreateProductModal;
